const runtimeConfig =
  typeof window !== 'undefined' && window.__HERMETIQ_RUNTIME_CONFIG__
    ? window.__HERMETIQ_RUNTIME_CONFIG__
    : {};

const readConfig = (key, fallback = '') => {
  const runtimeValue = runtimeConfig[key];
  if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
    return runtimeValue;
  }

  if (process.env.NODE_ENV === 'development') {
    const developmentValue = process.env[key];
    if (developmentValue !== undefined && developmentValue !== null && developmentValue !== '') {
      return developmentValue;
    }
  }

  return fallback;
};

const readBooleanConfig = (key, fallback = false) => {
  const value = readConfig(key, fallback ? 'true' : 'false');
  if (typeof value === 'boolean') {
    return value;
  }

  return String(value).trim().toLowerCase() === 'true';
};

const readIntConfig = (key, fallback = 0) => {
  const value = readConfig(key, String(fallback));
  const parsed = parseInt(value, 10);
  if (isNaN(parsed) || parsed < 1) {
    return fallback;
  }
  return parsed;
};

export const dataRetentionDays = readIntConfig('REACT_APP_DATA_RETENTION_DAYS', 30);
export const defaultTimeRangeConfig = readConfig('REACT_APP_DEFAULT_TIME_RANGE', '10080m');
export const isStytchEnabled = readBooleanConfig('REACT_APP_ENABLE_STYTCH', true);
// On-prem mode defaults to the inverse of Stytch auth. Both can be overridden independently.
export const isOnPremMode = readBooleanConfig('REACT_APP_ON_PREM', !isStytchEnabled);
export const isSaaSMode = !isOnPremMode;
// Optional on-prem surfaces are enabled by the deployment; SaaS keeps its defaults.
export const isAssistantEnabled = readBooleanConfig('REACT_APP_ENABLE_ASSISTANT', isSaaSMode);
export const isBbConfigEnabled = readBooleanConfig('REACT_APP_ENABLE_BB_CONFIG', isSaaSMode);
export const isVictoriaLogsEnabled = readBooleanConfig('REACT_APP_ENABLE_VICTORIA_LOGS', isSaaSMode);
// web-dev runs the production bundle too; rollout is a deployment setting.
export const isGuidedOnboardingEnabled = isSaaSMode && readBooleanConfig('REACT_APP_ONBOARDING_PROTOTYPE', false);
// PARTITION_INTERVAL selects Build History behavior for the deployment's DB
// partition width. "1h" (hourly, high-ingest sites) switches the grouped Recent
// Builds table to keyset "load more" pagination with approximate totals, which
// keeps the backend off the expensive COUNT(DISTINCT)/whole-window scan. Any
// other value ("1d" default) keeps the classic numbered pages + exact totals.
export const partitionInterval = String(readConfig('REACT_APP_PARTITION_INTERVAL', '1d')).trim().toLowerCase();
export const isHourlyPartitioned = partitionInterval === '1h';
// Error display mode: 'modal' (blocking popup), 'panel' (dismissable sidebar list), or 'none'.
// SaaS defaults to 'modal'; on-prem defaults to the less-invasive 'panel'.
const ERROR_DISPLAY_MODES = ['modal', 'panel', 'none'];
const defaultErrorDisplayMode = isOnPremMode ? 'panel' : 'modal';
const rawErrorDisplayMode = String(readConfig('REACT_APP_ERROR_DISPLAY', defaultErrorDisplayMode)).trim().toLowerCase();
export const errorDisplayMode = ERROR_DISPLAY_MODES.includes(rawErrorDisplayMode)
  ? rawErrorDisplayMode
  : defaultErrorDisplayMode;
export const isErrorModalEnabled = errorDisplayMode === 'modal';
export const isErrorPanelEnabled = errorDisplayMode === 'panel';
export const anonymousDashboardUserId = readConfig('ALLOW_ANONYMOUS_DASHBOARD_ACCESS');
export const isAnonymousDashboardEnabled = anonymousDashboardUserId.trim() !== '';
export const bbMcpUrl = readConfig('REACT_APP_BB_MCP_URL');
// Temporary internal rollout; the project/identity gate also applies when enabled.
export const isRbeUiEnabled = readBooleanConfig('REACT_APP_ENABLE_RBE_UI', true);
export const rbePortalLocalUrl = readConfig('REACT_APP_RBE_PORTAL_LOCAL_URL');
export const isE2ETestMode = readConfig('REACT_APP_E2E_TEST_MODE') === 'true';
// Integrated browser journeys keep the real authenticated route providers;
// individual editor specs retain their standalone harness by default.
export const isE2EAuthenticatedRoutesEnabled = isE2ETestMode && readBooleanConfig('REACT_APP_E2E_AUTHENTICATED_ROUTES', false);
// The Demo project's first-visit welcome opens on its own. E2E test mode turns
// that off by default so specs that sit on the Demo project are not
// interrupted; the walkthrough specs opt back in.
export const isWalkthroughAutoStartEnabled = isSaaSMode && readBooleanConfig('REACT_APP_WALKTHROUGH_AUTOSTART', !isE2ETestMode);
export const googleFormId = readConfig('REACT_APP_GOOGLE_FORM_ID');
export const grpcUnaryAttemptTimeoutMs = readIntConfig('REACT_APP_GRPC_UNARY_ATTEMPT_TIMEOUT_MS', 90000);
export const grpcWebUrl = readConfig('REACT_APP_GRPC_WEB_URL', 'https://api.cloud-grpc.hermetiq.io');
export const hermetiqAiPluginVersion = String(
  readConfig('REACT_APP_HERMETIQ_AI_PLUGIN_VERSION')
).trim();
export const hmqApiUrl = readConfig('REACT_APP_HMQ_API_URL', 'https://api.cloud-usc1.hermetiq.io');
export const hmqBesResultsUrl = readConfig('REACT_APP_HMQ_BES_RESULTS_URL', 'https://dashboard.hermetiq.io/build/');
export const hmqBesSvc = readConfig('REACT_APP_HMQ_BES_SVC', 'grpcs://api.cloud-grpc.hermetiq.io');
export const hmqMetricsDashboardUrl = readConfig('REACT_APP_HMQ_METRICS_DASHBOARD_URL');
export const hmqMcpServerUrl = readConfig('REACT_APP_HMQ_MCP_SERVER_URL', 'https://mcp.cloud-usc1.hermetiq.io/mcp');
export const hmqRemoteCache = readConfig(
  'REACT_APP_HMQ_REMOTE_CACHE',
  isOnPremMode ? '' : 'grpcs://lb.bb.cloud-grpc.hermetiq.io'
);
export const logRocketId = readConfig('REACT_APP_LOGROCKET_ID');
export const oauth2LogoutRedirectUrl = readConfig('REACT_APP_OAUTH2_LOGOUT_REDIRECT_URL');
export const isCostIntegrationEnabled = readBooleanConfig('REACT_APP_COST_INTEGRATION_ENABLED', false);
export const isSearchablesEnabled = readBooleanConfig('REACT_APP_SEARCHABLES_ENABLED', true);
export const stytchDomain = readConfig('REACT_APP_STYTCH_DOMAIN', 'login.cloud.hermetiq.io');
export const stytchProjectId = readConfig('REACT_APP_STYTCH_PROJECT_ID');
export const stytchPublicToken = readConfig('REACT_APP_STYTCH_PUBLIC_TOKEN');
export const targetTrendsMvRefreshJobsDisabled = readBooleanConfig('REACT_APP_TARGET_TRENDS_MV_REFRESH_JOBS_DISABLED', false);
